import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { AchievementManager } from '../utils/achievementManager';

const { FiPlay, FiTrophy, FiSettings, FiClock, FiInfinity, FiAward } = FiIcons;

function MenuScreen({ onNavigate, settings }) {
  const playerLevel = AchievementManager.getPlayerLevel();
  const unlockedAchievements = JSON.parse(localStorage.getItem('unlockedAchievements') || '[]');
  
  const menuItems = [
    {
      id: 'game',
      title: 'Start Game',
      icon: FiPlay,
      color: 'from-green-500 to-emerald-600',
      description: `${settings.difficulty.charAt(0).toUpperCase() + settings.difficulty.slice(1)} • ${settings.gameMode === 'timed' ? 'Timed' : 'Endless'}`
    },
    {
      id: 'achievements',
      title: 'Achievements',
      icon: FiAward,
      color: 'from-purple-500 to-pink-600',
      description: `${unlockedAchievements.length} unlocked • Level ${playerLevel.level}`
    },
    {
      id: 'leaderboard',
      title: 'Leaderboard',
      icon: FiTrophy,
      color: 'from-yellow-500 to-orange-600',
      description: 'View top scores'
    },
    {
      id: 'settings',
      title: 'Settings',
      icon: FiSettings,
      color: 'from-blue-500 to-indigo-600',
      description: 'Game preferences'
    }
  ];

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="text-center mb-8"
      >
        <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
          Color Mix
        </h1>
        <p className="text-xl text-gray-300">
          Match the target color with RGB sliders
        </p>
        <div className="flex items-center justify-center gap-2 mt-4">
          <SafeIcon icon={settings.gameMode === 'timed' ? FiClock : FiInfinity} className="text-gray-400" />
          <span className="text-gray-400 capitalize">
            {settings.gameMode} Mode • {settings.difficulty}
          </span>
        </div>
      </motion.div>

      {/* Player Level Display */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, delay: 0.2 }}
        className="bg-gradient-to-r from-purple-600/20 to-blue-600/20 backdrop-blur-sm rounded-2xl p-4 mb-8 w-full max-w-md border border-purple-400/30"
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-white font-bold text-lg">Level {playerLevel.level}</h3>
            <p className="text-gray-300 text-sm">{playerLevel.currentXP} XP</p>
          </div>
          <div className="text-right">
            <div className="text-2xl">🏆</div>
            <p className="text-gray-300 text-xs">{unlockedAchievements.length} achievements</p>
          </div>
        </div>
        
        <div className="w-full bg-gray-700 rounded-full h-2">
          <motion.div 
            className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${playerLevel.progress}%` }}
            transition={{ duration: 1, delay: 0.5 }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-400 mt-1">
          <span>{playerLevel.currentLevelXP} XP</span>
          <span>{playerLevel.nextLevelXP} XP</span>
        </div>
      </motion.div>

      <div className="w-full max-w-md space-y-4">
        {menuItems.map((item, index) => (
          <motion.button
            key={item.id}
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: index * 0.1 + 0.3 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => onNavigate(item.id)}
            className={`w-full bg-gradient-to-r ${item.color} p-6 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 relative overflow-hidden`}
          >
            {item.id === 'achievements' && unlockedAchievements.length > 0 && (
              <div className="absolute top-2 right-2 bg-yellow-400 text-black text-xs px-2 py-1 rounded-full font-bold">
                NEW
              </div>
            )}
            
            <div className="flex items-center gap-4">
              <div className="bg-white/20 p-3 rounded-xl">
                <SafeIcon icon={item.icon} className="text-2xl text-white" />
              </div>
              <div className="text-left">
                <h3 className="text-xl font-bold text-white">
                  {item.title}
                </h3>
                <p className="text-white/80 text-sm">
                  {item.description}
                </p>
              </div>
            </div>
          </motion.button>
        ))}
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="mt-8 text-center"
      >
        <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4">
          <p className="text-gray-300 text-sm">
            🎯 Match colors • 🏆 Earn achievements • 📈 Level up
          </p>
        </div>
      </motion.div>
    </div>
  );
}

export default MenuScreen;